<template>
  <c-box
    cursor="pointer"
    :_hover="{ background: 'white' }"
    overflow="hidden"
    @click="downloadFile"
    py="md"
    tabindex="0"
    @keydown.enter="downloadFile"
  >
    <c-box w="60px" float="left" pl="xs">
      <c-icon name="attachment" color="brand.purple" size="1.8rem" />
    </c-box>
    <c-box w="calc(100% - 60px - min(40%, 160px))" float="left">
      <c-text fontSize="md" lineHeight="1.5" mt="2xs">{{ attribute.label }}</c-text>
    </c-box>
    <c-box w="min(40%, 160px)" float="left">
      <c-box @click.stop>
        <c-select
          v-model="selectedLocale"
          size="sm"
          paddingLeft="lg"
          borderRadius="full"
          borderColor="brand.purple"
          w="100%"
        >
          <option
            v-for="(attributeValue, attributeName) in attribute.valueByLanguageCode"
            :key="attributeValue"
            :selected="attributeName === selectedLocale"
            :value="attributeName"
          >
            {{ languageByLocale(attributeName) }}
          </option>
        </c-select>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { IProductAttribute } from "@iloq/shared";
import { CSelect, CBox, CText, CIcon } from "@chakra-ui/vue";

export default defineComponent({
  name: "ProductFileRow",
  components: {
    CSelect,
    CBox,
    CText,
    CIcon,
  },
  props: {
    attribute: {
      type: Object as PropType<IProductAttribute>,
      required: true,
    },
    defaultLocale: {
      type: String,
      default: "en_US",
    },
    preferredLocale: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedLocale: "",
      allLangs: [
        {
          locale: "en_US",
          languageDisplay: "English (US)",
        },
        {
          locale: "en_GB",
          languageDisplay: "English (GB)",
        },
        {
          locale: "fi_FI",
          languageDisplay: "Suomi",
        },
        {
          locale: "de_DE",
          languageDisplay: "Deutsch",
        },
        {
          locale: "sv_SE",
          languageDisplay: "Svenska",
        },
        {
          locale: "nl_NL",
          languageDisplay: "Nederlands",
        },
        {
          locale: "da_DK",
          languageDisplay: "Dansk",
        },
        {
          locale: "fr_FR",
          languageDisplay: "Français",
        },
        {
          locale: "es_ES",
          languageDisplay: "Español",
        },
        {
          locale: "nb_NO",
          languageDisplay: "Norsk",
        },
        {
          locale: "pl_PL",
          languageDisplay: "Polski",
        },
        {
          locale: "sl_SI",
          languageDisplay: "Slovenščina",
        },
      ],
    };
  },
  created() {
    this.selectedLocale = this.initializeSelect();
  },
  methods: {
    regEx(value: string): string {
      const re = /(?:\.([^.]+))?$/;
      const res = re.exec(value);
      if (res) {
        return res[0];
      }
      return "";
    },
    initializeSelect(): string {
      let locale: string;
      if (this.attribute.valueByLanguageCode?.[this.preferredLocale]) {
        locale = this.preferredLocale;
      } else if (this.attribute.valueByLanguageCode?.[this.defaultLocale]) {
        locale = this.defaultLocale;
      } else {
        locale = Object.keys(this.attribute.valueByLanguageCode!)[0];
      }
      return locale;
    },
    selectLanguage(lang: string) {
      this.downloadFile();
    },
    languageByLocale(locale: string): string {
      const languageByLocale = this.allLangs.filter((item) => item.locale === locale);
      if (languageByLocale.length) {
        return languageByLocale[0].languageDisplay;
      }
      return locale.split("_").shift() as string; // Pick language code of unsupported locale
    },
    downloadFile() {
      if (this.attribute.valueByLanguageCode?.[this.selectedLocale]) {
        window.open(this.attribute.valueByLanguageCode[this.selectedLocale]);
      }
    },
  },
  watch: {
    selectedLocale: {
      handler(newLocale, oldLocale) {
        if (!oldLocale && newLocale === this.preferredLocale) {
          // Prevent watcher triggering download immediately on page load
          return;
        }
        this.selectLanguage(newLocale);
      },
      immediate: false,
    },
  },
});
</script>
